/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import { Box } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import React, { useState } from 'react';
import { graphManager } from '../../GraphManager';
import { useJSGNotification } from '../utils/JSGHooks';

const { StreamSheet, SetChartFormulaCommand, CellEditor, Strings, SheetCommandFactory, CellRange } = JSG;

export default function SheetEditBar(props) {
	const [cellEditor, setCellEditor] = useState();
	const [formulaText, setFormulaText] = useState();
	const [infoText, setInfoText] = useState();

	const onShapeSelection = () => {
		const viewer = graphManager.getGraphViewer();
		const selection = viewer.getSelection();

		graphManager.getGraphEditor().updateActiveView();

		const sheetView = graphManager.getActiveSheetView();
		const editor = CellEditor.getActiveCellEditor();

		if (editor || sheetView === undefined || selection.length !== 1) {
			return;
		}

		const item = selection[0].getModel();
		const sheet = sheetView.getItem();

		let text = selection[0].getView().getSelectedFormula(sheet);
		if (item instanceof JSG.TextNode) {
			text = Strings.decodeXML(text);
		}
		if (text === undefined) {
			text = '';
		}
		setFormulaText(text);
		// due to contenteditable
		const formula = document.getElementById('editbarformula');
		if (formula) {
			formula.innerHTML = text;
		}
		setInfoText(item.getName().getValue());
	};

	const onSheetSelection = (notification) => {
		const { item } = notification.object;

		if (cellEditor || !item || graphManager.getGraphViewer().getSelection().length) {
			return;
		}

		const view = graphManager.getActiveSheetView();
		if (!view || view.getItem() !== item) {
			return;
		}

		const activeCell = item.getOwnSelection().activeCell;

		if (!(item instanceof StreamSheet) || activeCell === undefined) {
			setFormulaText();
			setInfoText();
			return;
		}

		const text = view ? view.getEditString(activeCell) : '';
		setFormulaText(text);
		const formula = document.getElementById('editbarformula');
		if (formula) {
			formula.innerHTML = text;
		}

		if (item._editBarMode === 'ref') {
			setInfoText(item.getOwnSelection().refToString());
		} else {
			const sel = item.getOwnSelection();
			const range = sel.getRanges()[sel.getSize() - 1];
			if (range.height > 1 || range.width > 1) {
				const names = JSG.getLocalizedString('RC');
				setInfoText(`${range.height}${names[0]} x ${range.width}${names[1]}`);
			} else {
				setInfoText(item.getOwnSelection().refToString());
			}
		}
	};

	useJSGNotification(JSG.WorksheetNode.SELECTION_CHANGED_NOTIFICATION, onSheetSelection);
	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onShapeSelection);
	useJSGNotification('SHAPE_NOTIFICATION', onShapeSelection);

	const finishEditing = (text) => {
		const view = graphManager.getActiveSheetView();
		if (view === undefined) {
			return;
		}

		const graphEditor = graphManager.getGraphEditor();
		const canvas = graphManager.getCanvas();
		const selection = graphManager.getGraphViewer().getSelectionProvider();
		let data;
		const item = view.getItem();

		if (selection.hasSelection() && !selection.hasSingleSelection()) {
			return;
		}

		try {
			data = item.textToExpression(text);
			// TODO: validate its a graph formula, if expected
		} catch (e) {
			const element = document.getElementById('editbarformula');
			if (cellEditor) {
				cellEditor.deActivateReferenceMode();
			}
			view.notifyMessage({
				code: e.code,
				value: e.operand,
				message: e.message,
				focusElement: element,
				focusIndex: e.index !== undefined ? e.index + 1 : 1,
				start: e.start != null ? e.start + 1 : undefined,
			});
			return;
		}

		let cmd;
		if (selection.hasSelection()) {
			const graphItem = selection.getFirstSelection().getModel();
			const expr = data.expression;
			expr.evaluate(item);

			const chartView = selection.getFirstSelection().getView();
			if (chartView.isNewChart && chartView.hasSelectedFormula()) {
				if (chartView.chartSelection.element === 'plot') {
					const chart = chartView.getItem();
					const cmdChart = chart.prepareCommand('chart');
					chart.updateFormulas(graphManager.getGraphViewer(), expr.getFormula(), cmdChart);
				} else {
					cmd = new SetChartFormulaCommand(graphItem, chartView.chartSelection, data.expression);
				}
			} else {
				cmd = graphItem.termToPropertiesCommands(item.model, expr.getTerm());
			}
		} else {
			const ref = item.getOwnSelection().activeCellToString();
			if (data.numberFormat) {
				const cmdFormat = view.getNumberFormatCommand(
					graphManager.getGraphViewer(),
					view.getOwnSelection().getActiveCell(),
					data.numberFormat,
					data.localCulture
				);
				if (cmdFormat) {
					graphManager.synchronizedExecute(cmdFormat);
				}
			}
			// DL-2281:
			const activeCell = item.getOwnSelection().getActiveCell();
			cmd =
				activeCell.x === 1 && data.expression.getValue() === ''
					? SheetCommandFactory.create(
							'command.DeleteCellContentCommand',
							item.model,
							item.getOwnSelection().toStringMulti(),
							'all'
					  )
					: SheetCommandFactory.create('command.SetCellDataCommand', item.model, ref, data.expression, true);
		}

		canvas.focus();

		CellEditor.deActivateCellEditor();

		if (cmd !== undefined) {
			const viewer = graphManager.getGraphViewer();
			viewer.getInteractionHandler().execute(cmd, undefined, true);
			// graphManager.synchronizedExecute(cmd);
		}

		graphEditor.invalidate();
		window.getSelection().removeAllRanges();
		view.notify();
	};

	const handleReferenceKeyDown = (event) => {
		if (event.key !== 'Enter') {
			return;
		}

		const ref = document.getElementById('editbarreference');
		const sheetView = graphManager.getActiveSheetView();
		if (sheetView === undefined) {
			return;
		}
		const sheet = sheetView.getItem();
		const range = CellRange.parse(ref.textContent.toUpperCase(), sheet.model, true);
		if (range !== undefined) {
			const { graph } = graphManager;
			const viewer = graphManager.getGraphViewer();
			const index = graph.getSheetIndex(range.sheet);
			const streamsheetContainer = sheet.getStreamSheetContainer();

			streamsheetContainer.setActiveSheetIndex(index, viewer);
			const graphController = viewer.getGraphController();

			const controller = graphController.getControllerByModelId(sheet.getId());
			if (!controller) {
				return;
			}

			range.shiftFromSheet();
			const view = controller.getView();
			const selection = view.getOwnSelection();
			selection.selectRange(range.copy());
			view.showCell(selection.getActiveCell());
			view.notifySelectionChange(viewer);

			graphManager.redraw();
		}

		event.preventDefault();
		event.stopPropagation();
		const canvas = graphManager.getCanvas();
		canvas.focus();
	};

	const handleFunctionMouseDown = (event) => {
		if (!CellEditor.getActiveCellEditor()) {
			props.showDialog('InsertFunctionSettings');
		}
		event.preventDefault();
	};

	const handleFormulaFocus = () => {
		const view = graphManager.getActiveSheetView();
		if (view === undefined) {
			return;
		}

		let editor = CellEditor.getActiveCellEditor();
		if (!editor) {
			editor = CellEditor.activateCellEditor(
				document.getElementById('editbarformula'),
				graphManager.getGraphViewer(),
				view.getItem()
			);
			setCellEditor(editor);
		}
		if (editor) {
			editor.updateEditRangesView();
			editor.editBar = true;
			editor.focusOffset = window.getSelection().focusOffset;
		}
	};

	const handleFormulaBlur = (ev) => {
		if (!cellEditor) {
			return;
		}

		if (!cellEditor.div || cellEditor.div._ignoreBlur || (ev.relatedTarget && ev.relatedTarget.tagName === 'A')) {
			return;
		}

		finishEditing(cellEditor.div.textContent);
	};

	const handleFormulaKeyUp = (event) => {
		if (!cellEditor || event.keyCode === 18 || event.altKey) {
			event.preventDefault();
			return false;
		}

		switch (event.key) {
			case 'F2':
			case 'F3':
			case 'F4':
			case 'ArrowLeft':
			case 'ArrowUp':
			case 'ArrowRight':
			case 'ArrowDown':
			case 'Shift':
			case 'Control':
				break;
			default:
				cellEditor.updateEditRangesView();
				break;
		}

		cellEditor.updateFunctionInfo();

		return undefined;
	};

	const handleFormulaMouseDown = () => {
		if (cellEditor) {
			cellEditor.deActivateReferenceMode();
		}
	};

	const handleFormulaMouseUp = () => {
		if (cellEditor) {
			cellEditor.updateFunctionInfo();
		}
	};

	const handleFormulaDoubleClick = () => {
		if (cellEditor) {
			cellEditor.handleDoubleClick();
			cellEditor.deActivateReferenceMode();
		}
	};

	const handleFormulaSelect = () => {
		const selection = window.getSelection();
		if (cellEditor && selection.isCollapsed) {
			cellEditor.focusOffset = selection.focusOffset;
			cellEditor.focusNode = selection.focusNode;
		}
	};

	const handleFormulaKeyDown = (event) => {
		const view = graphManager.getActiveSheetView();
		if (view === undefined) {
			return;
		}

		const formula = document.getElementById('editbarformula');

		if (cellEditor.handleFunctionListKey(event, view)) {
			return;
		}

		switch (event.key) {
			case 'F2':
				cellEditor.toggleReferenceMode();
				break;
			case 'F3':
				cellEditor.showParamOptions(event, view);
				break;
			case 'F4':
				cellEditor.toggleReferenceType(event, view);
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
			case 'ArrowRight':
			case 'ArrowDown':
				if (cellEditor.isReferenceByKeyAllowed()) {
					const index = cellEditor.getSelectedRangeIndex();
					cellEditor.activateReferenceMode();
					if (index !== undefined) {
						cellEditor.setActiveRangeIndex(index);
					}
					cellEditor.updateReference(event, view);
				}
				break;
			default:
				break;
		}

		if (cellEditor.isReferenceChar(event.key)) {
			cellEditor.oldContent = undefined;
		}

		switch (event.key) {
			case 'Tab':
				event.preventDefault();
				break;
			case 'Space':
				if (event.altKey) {
					event.preventDefault();
					event.stopPropagation();
				}
				break;
			case 'Escape': {
				// ESC
				formula._ignoreBlur = true;
				window.getSelection().collapseToEnd();
				graphManager.getCanvas().focus();
				graphManager.getGraph().markDirty();
				CellEditor.deActivateCellEditor();
				const viewer = graphManager.getGraphViewer();
				if (viewer.getSelection().length) {
					viewer.getSelectionProvider()._notifySelectionChanged();
				} else {
					view.notify();
				}
				graphManager.redraw();
				formula._ignoreBlur = false;
				break;
			}
			case 'Enter':
				if (event.altKey) {
					// TODO
				} else {
					formula._ignoreBlur = true;
					finishEditing(event.target.textContent);
					event.preventDefault();
					event.stopPropagation();
					formula._ignoreBlur = false;
				}
				break;
			default:
				break;
		}
	};

	return (
		<AppBar
			elevation={0}
			color="default"
			tabIndex="-1"
			style={{
				backgroundColor: (theme) => theme.components.MuiAppBar.styleOverrides.colorEdit.backgroundColor,
				position: 'relative',
				margin: 0,
				fontSize: '9pt',
				display: 'block',
				height: '20px'
			}}
		>
			<span
				id="editbarreference"
				contentEditable={props.appEditable}
				suppressContentEditableWarning
				onKeyDown={handleReferenceKeyDown}
				style={{
					margin: 0,
					padding: '3px 3px 0px 3px',
					display: 'inline-block',
					position: 'relative',
					zIndex: 101,
					width: '152px',
					wordWrap: 'break-word',
					borderBottom: '1px solid lightgrey',
					borderLeft: '1px solid lightgrey',
					minHeight: '17px',
					maxHeight: '17px',
					overflow: 'hidden',
					verticalAlign: 'top'
				}}
			>
				{infoText || ''}
			</span>
			<Box
				id="editbarcommands"
				tabIndex="-1"
				onMouseDown={handleFunctionMouseDown}
				sx={{
					margin: 0,
					padding: '3px 3px 0px 3px',
					display: 'inline-block',
					position: 'relative',
					width: '102px',
					borderLeft: '1px solid lightgrey',
					borderBottom: '1px solid lightgrey',
					minHeight: '17px',
					verticalAlign: 'top',
					cursor: props.appEditable ? 'pointer' : 'arrow',
					'&:hover': {
						backgroundColor: `rgba(0, 0, 0, 0.04)`
					}
				}}
			>
				<span style={{ fontStyle: 'italic', fontWeight: 'bold', color: 'gray' }}>f</span> (x)
			</Box>
			<Box
				id="editbarformula"
				contentEditable={props.appEditable && formulaText !== undefined}
				onKeyDown={handleFormulaKeyDown}
				onKeyUp={handleFormulaKeyUp}
				onBlur={handleFormulaBlur}
				onFocus={handleFormulaFocus}
				onMouseDown={handleFormulaMouseDown}
				onMouseUp={handleFormulaMouseUp}
				onDoubleClick={handleFormulaDoubleClick}
				onSelect={handleFormulaSelect}
				spellCheck={false}
				sx={{
					margin: 0,
					padding: '3px 3px 0px 3px',
					display: 'inline-block',
					position: 'relative',
					right: '0px',
					width: 'calc(100% - 276px)',
					wordWrap: 'break-word',
					whiteSpace: 'pre-wrap',
					borderLeft: '1px solid lightgrey',
					borderRight: '1px solid lightgrey',
					borderBottom: '1px solid lightgrey',
					minHeight: '17px',
					backgroundColor: (theme) => theme.components.MuiAppBar.styleOverrides.colorEdit.backgroundColor,
					maxHeight: '200px',
					overflowY: 'auto',
					verticalAlign: 'top'
				}}
			/>
		</AppBar>
	);
}
